import { ApolloProvider } from "@apollo/client";
import { message } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import apolloClient from "../../apollo_client";
import LoginInterceptor from "./login_interceptor/login_interceptor";
import { getkey, identifyDevicePlatform, getQueryVariable, getDecodeUrl, isHadParam, setCookie, getCookie } from "@util/utils";
import { findTeamLanGuaGeInfo } from "@api/team";
import { queryTeamVipOpenStatus } from "@api/team_vip";
import "@common/common.less";
import "antd/dist/antd.less";
import "./login.less";
import shareMsg from "@assets/images/msg-share.png";
import useLogin from "./useLogin";
import loginContext from "@components/login_context";
import LangTab from "@components/lang_tab";
import { useTranslation } from "react-i18next";
import initI18N from "@util/initI18N";
import { getCurrentLanguageCode } from "@util/loginLanguage";
const fallbackLng = getkey('fallbackLng')||'zh'
initI18N(fallbackLng)

// 登录页要求「平板」统一按 PC 呈现（且不透出微信登录入口）。
// identifyDevicePlatform() 里 `os.isAndroid` 的分支会先命中：安卓平板拿到的是 "phone"，
// 只有 iPad 才会返回 "pad"。这里用仓库里 isTablet 的同一口径（iPad / 安卓不带 Mobile /
// Firefox Tablet）把平板补判回来，手机该走的还是手机端。
const looksLikeTabletUa = (ua) =>
  /(?:iPad)/.test(ua) ||
  (/(?:Android)/.test(ua) && !/(?:Mobile)/.test(ua)) ||
  (/(?:Firefox)/.test(ua) && /(?:Tablet)/.test(ua));
const normalizeTabletPlatform = (platform) => {
  if (platform !== "phone") {
    return platform;
  }
  return looksLikeTabletUa(navigator.userAgent || "") ? "pad" : platform;
};

export default function App() {
  const entrust = getkey("entrust");
  const clientId = getkey('clientId');
  const privateMemberTeamId = clientId && !["null", "undefined"].includes(clientId) ? clientId : "";
  const privateMemberInviterId = getkey("creatorId") || getkey("inviterId");
  const privateMemberSource = getkey("source");

  const callback = getQueryVariable('callback');
  // 第一次登录
  const [isLogin, setIslogin] = useState(false);

  const [hadParam, setHadParam] = useState(false);
  // pad（iPad / 安卓平板等）的登录页呈现跟随 PC，只有手机走手机端布局
  const [devicePlatform, setDevicePlatform] = useState(() =>
    normalizeTabletPlatform(identifyDevicePlatform())
  );
  const isPhone = devicePlatform === "phone";
  const isPad = devicePlatform === "pad";
  // 微信登录相关逻辑保持原样：手机 + 平板都按手机端那套走（只有 PC 例外），
  // 本次只调整了登录页的呈现，不动微信登录的链路
  const isPhoneWx = devicePlatform !== "pc";
  const [appHeight, setAppHeight] = useState(window.innerHeight);
  const [privateMemberOpen, setPrivateMemberOpen] = useState(false);
  const [privateMemberOpenStatusLoaded, setPrivateMemberOpenStatusLoaded] = useState(false);
  const [privateTeamLogo, setPrivateTeamLogo] = useState("");
  const [privateTeamInfo, setPrivateTeamInfo] = useState({});
  const [privateAgreementContracts, setPrivateAgreementContracts] = useState([]);
  const [wechatLoginVisible, setWechatLoginVisible] = useState(false);

  const { t, i18n } = useTranslation()

  const { firstLogin, getUrlByHadParam, getUrlByNolasturl, platform, identityType, loginApi, noParamUrl, handleSearch } = useLogin();
  useEffect(() => {
    if (!isPhone) {
      return
    }
    setAppHeight(window.innerHeight)
    const fn = () => {
      if (window.innerHeight > appHeight) {
        setAppHeight(window.innerHeight)
      }
    }
    window.addEventListener('resize', fn)

    return () => {
      window.removeEventListener('resize', fn)
    }
  }, [appHeight, isPhone])

  useEffect(() => {
    if (isLogin) {
      firstLogin();
    }
  }, [isLogin]);
  // 有团队跳到团队，没团队跳团队创建页

  useEffect(() => {
    let canceled = false;
    setPrivateMemberOpen(false);
    setPrivateTeamLogo("");
    setPrivateTeamInfo({});
    setPrivateAgreementContracts([]);

    if (!privateMemberTeamId) {
      setPrivateMemberOpenStatusLoaded(true);
      return () => {
        canceled = true;
      };
    }

    setPrivateMemberOpenStatusLoaded(false);
    queryTeamVipOpenStatus(privateMemberTeamId, i18n.language)
      .then((res) => {
        if (canceled) return;

        const openStatusResult = res?.result;
        const openStatus = !!(
          res?.success &&
          (typeof openStatusResult === "boolean"
            ? openStatusResult
            : openStatusResult?.openStatus)
        );
        setPrivateMemberOpen(openStatus);
        setPrivateMemberOpenStatusLoaded(true);

        if (!openStatus) {
          setPrivateAgreementContracts([]);
          return;
        }

        setPrivateTeamInfo({
          id: openStatusResult?.teamId || privateMemberTeamId,
          name: openStatusResult?.teamName || "",
          logo: openStatusResult?.teamLogo || "",
        });
        setPrivateTeamLogo(openStatusResult?.teamLogo || "");
        setPrivateAgreementContracts(
          Array.isArray(openStatusResult?.contracts) ? openStatusResult.contracts : []
        );

        findTeamLanGuaGeInfo(privateMemberTeamId)
          .then((teamRes) => {
            if (!canceled && teamRes?.success) {
              setPrivateTeamInfo(teamRes.result || {});
            }
          })
          .catch(() => {});
      })
      .catch(() => {
        if (!canceled) {
          setPrivateMemberOpen(false);
          setPrivateMemberOpenStatusLoaded(true);
          setPrivateAgreementContracts([]);
        }
      });

    return () => {
      canceled = true;
    };
  }, [i18n.language, privateMemberTeamId]);

  useEffect(() => {
    identifyDevicePlatform();
    const platFrom = localStorage.getItem('devicePlatform');
    setDevicePlatform(normalizeTabletPlatform(platFrom || "pc"));
    const deferLoginRedirect = getkey('deferLoginRedirect') === '1';

    let str = getUrlByHadParam();
    if (str) setHadParam(str);
    if (!getCookie("mtoken") && location.href.indexOf("/login.html?show_login") === -1) {
      window.__user__ = null;
      localStorage.setItem('user', '{}');
    } else if (str && !deferLoginRedirect) {
      window.location.replace(str);
    }
    setCookie('fromSquare', false, 7);
  }, [])
  const shopPlatFun = (token) => {
    if (platform === 'MATERIAL_MALL_H5' && callback) {
      sessionStorage.setItem('shop_callback', '');
      const new_url = getDecodeUrl(callback);
      const str = isHadParam(new_url);
      location.replace(new_url + str + 'token=' + token);
    }
    // if (isPrevious) {
    //   const url = localStorage.getItem("previousUrl");
    //   localStorage.removeItem('previousUrl')
    //   window.location.href = url

    // }

  }
  const loginSucFun = () => {
    sessionStorage.setItem("isFirst", "");

    let targetUrl = hadParam;
    if (targetUrl && getkey("page") === "team_join_link") {
      try {
        const redirect = new URL(targetUrl, window.location.origin);
        redirect.searchParams.set(
          "fallbackLng",
          getCurrentLanguageCode(i18n?.language)
        );
        targetUrl = `${redirect.pathname}${redirect.search}${redirect.hash}`;
      } catch {
        // 保留原有回跳地址，避免语言参数拼接异常阻断登录后的跳转。
      }
    }
    window.location.replace(targetUrl)
    // window.location.href = hadParam;
  }
  const loginSucCallback = (res) => {
    // 有特殊参数
    if (hadParam) {
      loginSucFun();
      return
    }
    if (
      sessionStorage.getItem("isFirst") === "1" ||
      res.result.firstRegistration
    ) {
      setIslogin(true);
      return;
    }
    noParamUrl(res);
  };

  const privateTeamName = useMemo(() => {
    return (
      privateTeamInfo?.mlName?.[i18n?.language] ||
      privateTeamInfo?.mlName?.zh ||
      privateTeamInfo?.name ||
      t("该私域")
    );
  }, [i18n?.language, privateTeamInfo, t]);

  const previewPrivateAgreement = (contractType) => {
    const contract = privateAgreementContracts.find(
      (item) => item?.type === contractType || item?.contractType === contractType
    );

    if (!contract?.url) {
      message.warning(t("协议模板未配置"));
      return;
    }

    window.open(`/agreement.html?fileUrl=${contract.url}`, "_blank");
  };
   
  return (
    <ApolloProvider client={apolloClient}>
      <div
        className={`bj-login bjweb-flex-center${isPhone ? " bj-login-phone-private" : ""}${isPad ? " bj-login-pad" : ""}${!isPhone && wechatLoginVisible ? " bj-login-wechat" : ""}`}
        style={isPhone ? { height: appHeight } : {}}
      >
        {!isPhone && !wechatLoginVisible && (
          <div className="login-lang-tab">
            <LangTab languageCodes={["zh", "en", "ja"]} />
          </div>
        )}
        {devicePlatform !== "pc" && clientId === entrust && entrust ? <div className="msg bjweb-flex-center">
          <div className="msgitem bjweb-flex-center"><img src={shareMsg} /></div>
          <div className="msgitem bjweb-flex-center">{t("请使用电脑打开链接！")}</div>
        </div> : <div className={"login"}>
          {/* <div
            className="bj-logo"
            style={{ color: "#333" }}
            onClick={isFirsrtLogin}
          >
            第一次登录
          </div> */}
          <loginContext.Provider
            value={{
              identityType,
              getUrl: getUrlByNolasturl,
              getUrlByHadParam,
              loginApi,
              noParamUrl,
              loginSucCallback,
              platform,
              handleSearch,
              devicePlatform,
              isPhone,
              isPhoneWx,
              privateMemberOpen,
              privateMemberOpenStatusLoaded,
              privateMemberTeamId,
              privateMemberInviterId,
              privateMemberSource,
              privateTeamName,
              privateTeamLogo,
              previewPrivateAgreement
            }}
          >
            <LoginInterceptor showVisible
              onWechatLoginVisible={setWechatLoginVisible}
              isShop={platform === 'MATERIAL_MALL_H5' && callback}
              shopPlatFun={shopPlatFun}></LoginInterceptor>
          </loginContext.Provider>
        </div>}
        {/* <div
          className={!isLogin ? "select-role" : "select-role select-role-show"}
        >
          <SelectRole />
        </div> */}
        <div className={isPhone ? "login-footer login-footer-phone" : "login-footer"}>
          {isPhone ? (
            <span className="login-footer-content">
              {t("Powered By Bujue")} · {t("浙ICP备17033477号-5")}
            </span>
          ) : t("浙ICP备17033477号-5")}
        </div>
      </div>
    </ApolloProvider>
  );
}
